import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type { EditSwimmerFormData, UseEditSwimmerFormProps } from "./EditSwimmerForm.types";
import { formSchema } from "./EditSwimmerForm.types";
import { convertToBackendDateFormat } from "../../../../shared/utils/dateUtils";

export const useEditSwimmerForm = ({
  onSubmit,
  initialData,
  initialRegistrationTimestamp,
  onRegistrationTimestampChange,
}: UseEditSwimmerFormProps) => {
  const form = useForm<EditSwimmerFormData>({
    mode: "onSubmit",
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || "",
      building_tower: initialData?.building_tower || "",
      building_apartment: initialData?.building_apartment || "",
      swimmer_type_id: initialData?.swimmer_type_id || "",
      registration_timestamp: initialData?.registration_timestamp || "",
    },
  });

  const { register, handleSubmit, control, setValue, watch, formState } = form;

  // Admin-only registration timestamp handling
  const registrationTimestamp = watch("registration_timestamp");

  const [registrationDateValue, registrationTimeValue] = useMemo(() => {
    const source = registrationTimestamp || initialRegistrationTimestamp || "";
    if (!source) return ["", ""];
    const match =
      source &&
      source.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!match) return ["", ""];
    const [, year, month, day, hours, minutes] = match;
    return [`${year}-${month}-${day}`, `${hours}:${minutes}`];
  }, [registrationTimestamp, initialRegistrationTimestamp]);

  const registrationDateDisplay = useMemo(
    () => convertToBackendDateFormat(registrationDateValue),
    [registrationDateValue],
  );

  const handleRegistrationTimestampChange = (
    nextDateValue: string,
    nextTimeValue: string,
  ) => {
    if (!nextDateValue) {
      setValue("registration_timestamp", "", { shouldValidate: false });
      onRegistrationTimestampChange?.(null);
      return;
    }
    const timePart = nextTimeValue || "00:00";
    const iso = `${nextDateValue}T${timePart}`;
    setValue("registration_timestamp", iso, { shouldValidate: false });
    onRegistrationTimestampChange?.(iso);
  };

  const onFormSubmit = (data: EditSwimmerFormData) => {
    onSubmit(data);
  };

  return {
    register,
    handleSubmit: handleSubmit(onFormSubmit),
    control,
    errors: formState.errors,
    isValid: formState.isValid,
    registrationDateValue,
    registrationTimeValue,
    registrationDateDisplay,
    handleRegistrationTimestampChange,
  };
};
